import { MessageSquare, Plus, Send, Trash2 } from 'lucide-react';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DeleteChatDialog } from '../../components/delete-chat-dialog';
import { GuestUpgradeModal } from '../../components/guest-upgrade-modal';
import { StarterPrompts } from '../../components/starter-prompts/starter-prompts';
import { Textarea } from '../../components/textarea';
import { Tooltip } from '../../components/tooltip';
import { useGuestChatState } from '../../hooks/use-guest-chat-state';
import {
  GUEST_CHAT_LIMIT,
  type GuestMessage,
  type GuestSession,
} from '../../stores/guest-chat-store';
import { cn } from '../../utils/cn';
import { formatRelativeTime } from '../../utils/time-format';

// ─── Session Sidebar ──────────────────────────────────────────────────────────

const TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

type GuestSessionSidebarProps = {
  sessions: GuestSession[];
  activeSessionId: string | null;
  chatCreationCount: number;
  canCreateChat: boolean;
  onNewChat: () => void;
  onSelectSession: (id: string) => void;
  onDeleteSession: (id: string, title: string) => void;
};

const GuestSessionSidebar: React.FC<GuestSessionSidebarProps> = ({
  sessions,
  activeSessionId,
  chatCreationCount,
  canCreateChat,
  onNewChat,
  onSelectSession,
  onDeleteSession,
}) => {
  return (
    <div className="w-60 glass-card-bg glass-card-border rounded-2xl flex flex-col flex-shrink-0 overflow-hidden sidebar-animate-in">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 p-3 pb-2">
        <Tooltip
          content={
            canCreateChat
              ? 'New Chat'
              : `Limit reached (${GUEST_CHAT_LIMIT}/${GUEST_CHAT_LIMIT}). Sign in for unlimited chats.`
          }
          side="right"
          delay={300}
        >
          <button
            type="button"
            onClick={onNewChat}
            disabled={!canCreateChat}
            className={cn(
              'h-9 w-9 flex items-center justify-center rounded-lg',
              'border border-border',
              'hover:bg-accent hover:border-accent-foreground/20',
              'active:scale-95 transition-all duration-200',
              'text-foreground',
              'disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100'
            )}
            aria-label="New chat"
          >
            <Plus className="w-5 h-5" />
          </button>
        </Tooltip>

        <span className="text-xs text-muted-foreground font-medium">Chats</span>
      </div>

      {/* Session List */}
      <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-0.5">
        {sessions.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-2 py-8 px-3">
            <MessageSquare className="w-6 h-6 text-muted-foreground/40" />
            <p className="text-xs text-muted-foreground/60 text-center">Start your first chat</p>
          </div>
        )}

        {sessions.map(session => (
          <GuestSessionItem
            key={session.id}
            session={session}
            isActive={session.id === activeSessionId}
            onSelect={() => onSelectSession(session.id)}
            onDelete={() => onDeleteSession(session.id, session.title)}
          />
        ))}
      </div>

      {/* Usage Counter */}
      <div className="px-3 py-2.5 border-t border-border/40">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">
            Free plan
          </span>
          <span
            className={cn(
              'text-[10px] font-semibold tabular-nums',
              chatCreationCount >= GUEST_CHAT_LIMIT
                ? 'text-destructive/80'
                : 'text-muted-foreground'
            )}
          >
            {chatCreationCount} / {GUEST_CHAT_LIMIT}
          </span>
        </div>
        <div className="w-full h-1 bg-border/50 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-500',
              chatCreationCount >= GUEST_CHAT_LIMIT ? 'bg-destructive/70' : 'bg-primary/60'
            )}
            style={{ width: `${Math.min((chatCreationCount / GUEST_CHAT_LIMIT) * 100, 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
};

type GuestSessionItemProps = {
  session: GuestSession;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
};

const GuestSessionItem: React.FC<GuestSessionItemProps> = ({
  session,
  isActive,
  onSelect,
  onDelete,
}) => {
  const [showDelete, setShowDelete] = React.useState(false);

  return (
    <div
      className={cn(
        'group relative flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer',
        'transition-all duration-150',
        isActive
          ? 'bg-primary/10 text-foreground'
          : 'hover:bg-accent/50 text-muted-foreground hover:text-foreground'
      )}
      onClick={onSelect}
      onMouseEnter={() => setShowDelete(true)}
      onMouseLeave={() => setShowDelete(false)}
    >
      <MessageSquare
        className={cn(
          'w-3.5 h-3.5 flex-shrink-0',
          isActive ? 'text-primary' : 'text-muted-foreground/60'
        )}
      />

      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate leading-tight">{session.title}</p>
        <p className="text-[10px] text-muted-foreground/50 mt-0.5">
          {formatRelativeTime(session.updatedAt)}
        </p>
      </div>

      {showDelete && (
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            onDelete();
          }}
          className={cn(
            'flex-shrink-0 p-1 rounded-md',
            'text-muted-foreground/60 hover:text-destructive',
            'hover:bg-destructive/10 transition-colors'
          )}
          aria-label="Delete chat"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

// ─── Message Bubble ───────────────────────────────────────────────────────────

const LoadingDots: React.FC = () => (
  <div className="flex items-center gap-1 py-1">
    {[0, 1, 2].map(i => (
      <div
        key={i}
        className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce"
        style={{ animationDelay: `${i * 0.15}s`, animationDuration: '1s' }}
      />
    ))}
  </div>
);

const GuestMessageBubble: React.FC<{ message: GuestMessage }> = ({ message }) => {
  const isUser = message.sender === 'user';
  const isLoading =
    !isUser &&
    (message.status === 'processing' ||
      (message.status === 'streaming' && !message.content?.trim()));

  // Status text shown during MCP/tool calls
  const statusMatch = message.content?.match(/^_(.+)_$/);
  const statusText = statusMatch ? statusMatch[1] : null;

  if (isUser) {
    return (
      <div className="w-full mb-2 flex justify-end">
        <div className="max-w-[80%] rounded-2xl px-4 py-3 text-sm bg-primary/10 text-foreground border border-primary/20 shadow-sm">
          <div className="whitespace-pre-wrap break-words leading-relaxed">{message.content}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mb-4 pr-4">
      {isLoading ? (
        statusText ? (
          <p className="text-xs italic text-muted-foreground/70 animate-pulse">{statusText}</p>
        ) : (
          <LoadingDots />
        )
      ) : (
        <div className="prose prose-sm dark:prose-invert max-w-none text-foreground/90 text-sm leading-relaxed">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ hr: () => null }}>
            {message.content}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

// ─── Chat Area (messages + input) ────────────────────────────────────────────

type GuestChatAreaProps = {
  session: GuestSession | null;
  draftMessage: string;
  isLoading: boolean;
  onChangeDraft: (value: string) => void;
  onSubmit: (value?: string) => void;
};

const GuestChatArea: React.FC<GuestChatAreaProps> = ({
  session,
  draftMessage,
  isLoading,
  onChangeDraft,
  onSubmit,
}) => {
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // Scroll to bottom when new messages arrive
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [session?.messages.length, session?.messages.at(-1)?.content]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (draftMessage.trim() && !isLoading) {
        onSubmit();
      }
    }
  };

  const handleSubmit = () => {
    if (draftMessage.trim() && !isLoading) {
      onSubmit();
    }
  };

  const messages = session?.messages ?? [];

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0">
        {messages.length === 0 ? (
          <StarterPrompts onPromptClick={prompt => onSubmit(prompt)} />
        ) : (
          <>
            {messages.map(message => (
              <GuestMessageBubble key={message.id} message={message} />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 px-4 pb-4 pt-2">
        <div className="relative">
          <Textarea
            ref={textareaRef}
            value={draftMessage}
            onChange={e => onChangeDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isLoading ? 'Waiting for response...' : 'Ask anything...'}
            disabled={isLoading}
            rows={3}
            className="w-full resize-none min-h-[60px] max-h-[160px] pr-12"
          />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!draftMessage.trim() || isLoading}
            className={cn(
              'absolute bottom-3 right-3',
              'h-8 w-8 flex items-center justify-center rounded-full',
              'glass-button text-white',
              'transition-all duration-200 active:scale-95',
              'disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100'
            )}
            aria-label="Send message"
          >
            {isLoading ? (
              <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground/40 mt-1.5 text-center">
          Enter to send · Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};

// ─── Main View ────────────────────────────────────────────────────────────────

type GuestChatViewProps = {
  showSessionPanel?: boolean;
  onSessionSelect?: () => void;
};

export const GuestChatView: React.FC<GuestChatViewProps> = ({
  showSessionPanel = true,
  onSessionSelect,
}) => {
  const {
    sessions,
    activeSession,
    activeSessionId,
    chatCreationCount,
    canCreateChat,
    draftMessage,
    isLoading,
    newChat,
    openSession,
    deleteSession,
    updateDraftMessage,
    submitMessage,
  } = useGuestChatState();

  const initialChatCreated = React.useRef(false);

  // Open or create a chat on first mount if there's nothing active
  React.useEffect(() => {
    if (initialChatCreated.current) return;
    initialChatCreated.current = true;

    if (sessions.length > 0 && !activeSessionId) {
      openSession(sessions[0]!.id);
    } else if (sessions.length === 0 && canCreateChat) {
      newChat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Upgrade modal ────────────────────────────────────────────────────────────
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = React.useState(false);
  const messagesSentCount = React.useRef(0);

  // Trigger after 5 minutes; restarts whenever the modal is closed.
  React.useEffect(() => {
    if (isUpgradeModalOpen) return;
    const timer = setTimeout(() => setIsUpgradeModalOpen(true), TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isUpgradeModalOpen]);

  // Wrap submitMessage to count sent messages and trigger after 2
  const handleSubmitMessage = React.useCallback(
    (value?: string) => {
      submitMessage(value);
    },
    [submitMessage]
  );

  const [deleteDialog, setDeleteDialog] = React.useState<{
    isOpen: boolean;
    sessionId: string;
    sessionTitle: string;
  }>({ isOpen: false, sessionId: '', sessionTitle: '' });

  const handleDeleteOpen = (sessionId: string, sessionTitle: string) => {
    setDeleteDialog({ isOpen: true, sessionId, sessionTitle });
  };

  const handleDeleteConfirm = () => {
    deleteSession(deleteDialog.sessionId);
    setDeleteDialog({ isOpen: false, sessionId: '', sessionTitle: '' });
  };

  return (
    <div className="flex flex-1 h-full shrink-0 relative flex-col">
      <div className="w-full h-full flex overflow-hidden rounded-lg p-1 sm:p-3 gap-1 sm:gap-3 bg-background">
        {/* Session Sidebar */}
        {showSessionPanel && (
          <GuestSessionSidebar
            sessions={sessions}
            activeSessionId={activeSessionId}
            chatCreationCount={chatCreationCount}
            canCreateChat={canCreateChat}
            onNewChat={newChat}
            onSelectSession={id => {
              openSession(id);
              onSessionSelect?.();
            }}
            onDeleteSession={handleDeleteOpen}
          />
        )}

        {/* Chat Area */}
        <div className="glass-card rounded-2xl flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
          <GuestChatArea
            session={activeSession}
            draftMessage={draftMessage}
            isLoading={isLoading}
            onChangeDraft={updateDraftMessage}
            onSubmit={handleSubmitMessage}
          />
        </div>
      </div>

      {/* Delete Confirmation */}
      <DeleteChatDialog
        isOpen={deleteDialog.isOpen}
        sessionTitle={deleteDialog.sessionTitle}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ isOpen: false, sessionId: '', sessionTitle: '' })}
      />

      {/* Upgrade Modal */}
      <GuestUpgradeModal isOpen={isUpgradeModalOpen} onClose={() => setIsUpgradeModalOpen(false)} />
    </div>
  );
};
